import { useState } from 'react';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgentKind, AgentView } from '@app/shared';
import {
  Button,
  Cluster,
  ConfirmDialog,
  Dialog,
  Icon,
  Icons,
  type LucideIcon,
  PageHeader,
  Radio,
  RadioGroup,
  Stack,
  StatusBadge,
  Surface,
  Switch,
  Text,
  TextInput,
} from '../ui';
import { api, ApiError } from '../lib/api';
import { formatNextRun } from '../lib/nextRun';
import { deriveRunHealth, type RunHealthTone } from '../lib/runHealth';
import { qk } from '../lib/queryKeys';
import { useScopedWorkspace } from '../lib/operatorWorkspace';
import { useSupportEditConfirm } from '../lib/operatorEdit';
import { QueryState } from '../components/QueryState';
import { RunStatusBadge } from '../components/RunStatusBadge';
import { OperatorWorkspaceSelect } from '../components/OperatorWorkspaceSelect';
import { SupportModeBanner } from '../components/SupportModeBanner';
import { AGENT_KIND_LABELS } from './agentBuilderDefaults';

const agentsRoute = getRouteApi('/app/agents');

/**
 * The three agent kinds a user can create, in the order they appear in the picker.
 * `kind` is immutable after create (§13.1/§13.3), so it is chosen here, once, and the
 * one-line description tells the user what each kind does before they commit.
 */
const KIND_OPTIONS: { kind: AgentKind; description: string }[] = [
  { kind: 'inbox', description: 'Triage and draft on your mailbox.' },
  { kind: 'research', description: 'Produce a multi-source research report.' },
  { kind: 'task', description: 'Goal-directed agent that can act.' },
];

/** A sensible starting name per kind, prefilled when the picker switches. */
const DEFAULT_NAME: Record<AgentKind, string> = {
  inbox: 'Inbox Agent',
  research: 'Research Agent',
  task: 'Task Agent',
};

/**
 * Leading glyph for the run-health badge (#275), by tone. Colour is never the only cue —
 * the icon + label carry the meaning too, mirroring the `RunStatusBadge` icon vocabulary.
 */
const HEALTH_ICON: Record<RunHealthTone, LucideIcon> = {
  success: Icons.CheckCircle2,
  danger: Icons.AlertCircle,
  info: Icons.RefreshCw,
  warning: Icons.Hourglass,
  neutral: Icons.Circle,
};

/**
 * Agent list with status (§14 operations). Each row links to the builder. A single
 * "New agent" action opens one dialog with a name field + a kind picker (Inbox /
 * Research / Task); an agent's `kind` is fixed at creation (§13.1/§13.3) so it is
 * chosen here. A published, active research or task agent gets an inline "Run" that
 * starts a manual run and navigates to it.
 */
export function AgentsListPage() {
  // #278: a platform operator can VIEW another workspace's agents by selecting it in the
  // dropdown; the choice rides the `ws` search param so it survives navigation and Back. A
  // non-operator has no dropdown and `ws` is ignored — `workspaceId` stays their own.
  const { ws } = agentsRoute.useSearch();
  const routeNavigate = agentsRoute.useNavigate();
  const { workspaceId, ownWorkspaceId, viewOnly, isOperator, selected } = useScopedWorkspace(ws);
  // #326: a platform OPERATOR viewing a FOREIGN workspace may now EDIT it (customer
  // support). `supportMode` unlocks the create/edit/status affordances that `viewOnly`
  // used to hide; `readOnly` is the residual (a non-operator can never reach a foreign ws,
  // but stays hidden defensively). Edits carry the foreign `ws` so requests hit that tenant.
  const supportMode = isOperator && viewOnly;
  const canEdit = !viewOnly || supportMode;
  const readOnly = viewOnly && !supportMode;
  // Guardrail c (#326): confirm before the operator's FIRST edit in this foreign workspace.
  const editConfirm = useSupportEditConfirm(workspaceId, supportMode);
  // Search to carry on links/navigation into the builder so it scopes to the foreign ws.
  const editSearch = supportMode ? { ws: workspaceId } : undefined;
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Selecting the own workspace omits `ws` from the URL (own-workspace URLs stay clean);
  // any other workspace is written so the operator view persists across hops.
  const onWorkspaceChange = (id: string): void =>
    void routeNavigate({ search: (prev) => ({ ...prev, ws: id === ownWorkspaceId ? undefined : id }) });
  const [kind, setKind] = useState<AgentKind>('inbox');
  const [name, setName] = useState(DEFAULT_NAME.inbox);
  // v1.11.0 (#215): archived agents are hidden from the list unless this is on.
  const [showArchived, setShowArchived] = useState(false);

  const agents = useQuery({
    queryKey: [...qk.agents(workspaceId), { includeArchived: showArchived }] as const,
    queryFn: () =>
      api.listAgents(workspaceId, { pageSize: 100, includeArchived: showArchived || undefined }),
  });

  const create = useMutation({
    mutationFn: (input: { name: string; kind: AgentKind }) =>
      api.createAgent(workspaceId, { name: input.name.trim(), kind: input.kind }),
    onSuccess: async (agent) => {
      setDialogOpen(false);
      // Carry the foreign `ws` so the builder stays scoped to that tenant in support mode.
      await navigate({ to: '/app/agents/$agentId', params: { agentId: agent.id }, search: editSearch });
    },
  });

  const openDialog = () => {
    setKind('inbox');
    setName(DEFAULT_NAME.inbox);
    create.reset();
    setDialogOpen(true);
  };

  // Switching kind swaps the default name only while it is still a pristine default,
  // so a name the user typed is never clobbered.
  const selectKind = (next: AgentKind) => {
    setName((prev) => (Object.values(DEFAULT_NAME).includes(prev) ? DEFAULT_NAME[next] : prev));
    setKind(next);
  };

  return (
    <Stack gap="6">
      <PageHeader
        title="Agents"
        description="Autonomous agents in this workspace"
        actions={
          <Cluster gap="3" align="center" wrap>
            {/* #278: operator-only workspace dropdown (self-hides for a non-operator). */}
            <OperatorWorkspaceSelect
              value={workspaceId}
              ownWorkspaceId={ownWorkspaceId}
              onChange={onWorkspaceChange}
              // #326: operators can EDIT foreign agents here — the support banner conveys the
              // mode, so suppress the misleading "read-only" badge (kept on the Runs pages).
              showForeignBadge={false}
            />
            {/* v1.11.0 (#215): reveal archived agents (hidden by default). */}
            <Switch
              checked={showArchived}
              onChange={(e) => setShowArchived(e.currentTarget.checked)}
              label="Show archived"
              data-testid="agents-show-archived"
            />
            {/* Creating an agent is owner/operator-gated (#326); shown on the own ws OR when
                an operator is in support mode on a foreign ws. Guarded by the first-edit confirm. */}
            {canEdit && (
              <Button
                iconStart={<Icon as={Icons.Plus} size="sm" />}
                onClick={() => editConfirm.guard(openDialog)}
                data-testid="new-agent"
              >
                New agent
              </Button>
            )}
          </Cluster>
        }
      />

      {/* Guardrail b (#326): persistent support-mode banner while an operator edits a foreign ws. */}
      {supportMode && <SupportModeBanner workspace={selected} />}

      <QueryState
        query={agents}
        empty={(d) => d.data.length === 0}
        emptyState={{
          icon: <Icon as={Icons.Bot} />,
          title: readOnly ? 'No agents in this workspace' : 'No agents yet',
          description: readOnly
            ? 'This workspace has no agents yet.'
            : supportMode
              ? 'This customer has no agents yet. You can create one for them in support mode.'
              : 'Create your first agent to automate inbox triage, research, or a task.',
          // The create action is owner/operator-gated — shown on own ws or in support mode.
          action: canEdit
            ? {
                label: 'Create agent',
                onClick: () => editConfirm.guard(openDialog),
                testId: 'empty-create-agent',
              }
            : undefined,
        }}
      >
        {(data) => (
          <Stack gap="3">
            {data.data.map((agent) => (
              <AgentRow
                key={agent.id}
                agent={agent}
                workspaceId={workspaceId}
                viewOnly={viewOnly}
                canEdit={canEdit}
                editSearch={editSearch}
                guardEdit={editConfirm.guard}
              />
            ))}
          </Stack>
        )}
      </QueryState>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="New agent"
        size="sm"
        data-testid="new-agent-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate({ name, kind })}
              disabled={!name.trim() || create.isPending}
              loading={create.isPending}
              data-testid="new-agent-submit"
            >
              Create
            </Button>
          </>
        }
      >
        <Stack gap="5">
          <Stack gap="1">
            <Text as="label" id="new-agent-name-label" size="sm" weight="medium">
              Name
            </Text>
            <TextInput
              aria-labelledby="new-agent-name-label"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="new-agent-name"
            />
          </Stack>

          <Stack gap="2">
            <Text as="span" id="new-agent-kind-label" size="sm" weight="medium">
              Kind
            </Text>
            <RadioGroup
              name="new-agent-kind"
              aria-labelledby="new-agent-kind-label"
              value={kind}
              onValueChange={(value) => selectKind(value as AgentKind)}
            >
              {KIND_OPTIONS.map((opt) => (
                <Radio
                  key={opt.kind}
                  value={opt.kind}
                  data-testid={`kind-${opt.kind}`}
                  label={
                    <Stack gap="none">
                      <Text size="sm">{AGENT_KIND_LABELS[opt.kind]}</Text>
                      <Text size="xs" tone="secondary">
                        {opt.description}
                      </Text>
                    </Stack>
                  }
                />
              ))}
            </RadioGroup>
          </Stack>

          {create.isError && (
            <Text tone="danger" size="sm" data-testid="create-error">
              {create.error instanceof ApiError ? create.error.message : 'Create failed.'}
            </Text>
          )}
        </Stack>
      </Dialog>

      {/* Guardrail c (#326): first-edit confirm for support mode (list-level actions). */}
      <ConfirmDialog
        open={editConfirm.confirmOpen}
        onClose={editConfirm.cancel}
        onConfirm={editConfirm.confirm}
        title="Edit another customer's agents?"
        tone="danger"
        confirmLabel="I understand — continue"
        confirmTestId="support-edit-confirm"
        description={
          <Text tone="secondary" size="md">
            You're about to edit <strong>{selected?.name ?? 'another workspace'}</strong> as a
            platform operator. Changes you make apply to this customer's account. This confirmation
            is shown once per workspace for this session.
          </Text>
        }
      />
    </Stack>
  );
}

/**
 * One agent row. The card body links to the builder; a research or task agent that is
 * published + active exposes an inline Run (a sibling of the link, so the two remain
 * independently clickable — no nested interactive controls).
 */
function AgentRow({
  agent,
  workspaceId,
  viewOnly,
  canEdit,
  editSearch,
  guardEdit,
}: {
  agent: AgentView;
  workspaceId: string;
  // #278: an operator viewing a FOREIGN workspace. Manual "Run" (the trigger route, NOT
  // widened by #326) stays own-ws only, so it keys off `viewOnly`.
  viewOnly: boolean;
  // #326: whether the caller may edit this workspace's agents (own ws, or an operator in
  // support mode). Drives the builder link + Archive/unarchive.
  canEdit: boolean;
  // Search carried into the builder link so support-mode edits scope to the foreign ws.
  editSearch: { ws: string } | undefined;
  // Guardrail c (#326): run the given edit action behind the first-edit support confirm.
  guardEdit: (action: () => void) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // v1.11.0 (#215): confirm before archiving (unarchive is a safe, reversible no-confirm).
  const [confirmArchive, setConfirmArchive] = useState(false);
  const isArchived = agent.status === 'archived';

  const run = useMutation({
    mutationFn: () => api.triggerAgent(workspaceId, agent.id),
    onSuccess: (res) => navigate({ to: '/app/runs/$runId/full', params: { runId: res.runId } }),
  });

  // Archive / unarchive (v1.11.0, #215). Owner-only (the status route is owner-gated);
  // on success refresh every agents-list view (shared `['agents', ws]` prefix).
  const setStatus = useMutation({
    mutationFn: (action: 'archive' | 'unarchive') =>
      api.updateAgentStatus(workspaceId, agent.id, action),
    onSuccess: async () => {
      setConfirmArchive(false);
      await queryClient.invalidateQueries({ queryKey: qk.agents(workspaceId) });
    },
  });

  // Manual "Run" shows for any published + active agent that has a manually-invocable
  // (non-event) trigger (#264). Research/task agents are always manually runnable
  // (§13.3 mirrors research's manual-run behavior for task); a scheduled agent of any
  // kind is fireable too — `nextRunAt` (present only for an active enabled schedule)
  // signals a `schedule` trigger. An event-only agent has nothing to act on, so no Run.
  // An archived agent is not active, so it is never runnable.
  const runnable =
    !viewOnly &&
    agent.status === 'active' &&
    !!agent.currentVersionId &&
    (agent.kind === 'research' || agent.kind === 'task' || !!agent.nextRunAt);

  // Next scheduled run (v1.13.5): the server sends `nextRunAt` only for an active agent
  // with an enabled schedule; format it relative + absolute in the viewer's local time.
  const nextRun = formatNextRun(agent.nextRunAt);

  // Run-health (#275): derive a health indicator from the agent's MOST RECENT run. A
  // failing/blocked last run also danger-highlights the whole row so it stands out; the
  // `endedReason` (the "why") rides along as the badge tooltip when present.
  const health = deriveRunHealth(agent.lastRunStatus);
  const healthTooltip =
    health.failing && agent.lastRunEndedReason ? agent.lastRunEndedReason : undefined;

  // The row's identity block — the same content whether it links into the builder (own
  // workspace) or renders as a static summary (an operator's foreign, view-only workspace).
  const rowBody = (
    <Stack gap="1">
      <Cluster gap="2" align="center">
        <Text weight="semibold" size="lg" truncate>
          {agent.name}
        </Text>
        <StatusBadge
          tone="neutral"
          size="sm"
          label={AGENT_KIND_LABELS[agent.kind]}
          data-testid="agent-row-kind"
        />
        {isArchived && (
          <StatusBadge tone="neutral" size="sm" label="Archived" data-testid="agent-row-archived" />
        )}
      </Cluster>
      <Text size="sm" tone="secondary">
        {agent.currentVersionId ? 'Published' : 'Draft — not published'}
      </Text>
      {nextRun && (
        <Cluster
          gap="1"
          align="center"
          data-testid="agent-row-next-run"
          title={`Next run: ${nextRun.title}`}
        >
          <Icon as={Icons.Clock} size="xs" tone="muted" />
          <Text size="xs" tone="secondary">
            Next run: {nextRun.label}
          </Text>
        </Cluster>
      )}
    </Stack>
  );

  return (
    <Surface
      bordered
      radius="lg"
      data-testid="agent-row"
      highlight={health.failing ? 'danger' : undefined}
    >
      <Cluster gap="2" align="center" wrap={false} style={{ paddingInlineEnd: '1rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {canEdit ? (
            // Own workspace OR an operator in support mode (#326): the row links into the
            // builder (carrying the foreign `ws` in support mode so the builder stays scoped).
            <Link
              to="/app/agents/$agentId"
              params={{ agentId: agent.id }}
              search={editSearch}
              style={{ textDecoration: 'none', color: 'inherit', display: 'block', padding: '1rem' }}
            >
              {rowBody}
            </Link>
          ) : (
            // A read-only foreign workspace: the row is a static, non-navigating summary.
            <div style={{ padding: '1rem' }} data-testid="agent-row-static">
              {rowBody}
            </div>
          )}
        </div>
        {runnable && (
          <Button
            variant="secondary"
            size="sm"
            iconStart={<Icon as={Icons.Play} size="sm" />}
            onClick={() => run.mutate()}
            disabled={run.isPending}
            data-testid="agent-row-run"
          >
            Run
          </Button>
        )}
        {/* Archive/unarchive is owner/operator-gated (#326) — shown on own ws or in support
            mode, each behind the first-edit support confirm. */}
        {canEdit &&
          (isArchived ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => guardEdit(() => setStatus.mutate('unarchive'))}
              disabled={setStatus.isPending}
              data-testid="agent-row-unarchive"
            >
              Unarchive
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => guardEdit(() => setConfirmArchive(true))}
              disabled={setStatus.isPending}
              data-testid="agent-row-archive"
            >
              Archive
            </Button>
          ))}
        <StatusBadge
          tone={health.tone}
          size="sm"
          icon={HEALTH_ICON[health.tone]}
          label={health.label}
          data-testid="agent-run-health"
          title={healthTooltip}
        />
        <RunStatusBadge status={agent.status} />
      </Cluster>

      <ConfirmDialog
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={() => setStatus.mutate('archive')}
        title="Archive agent?"
        tone="danger"
        confirmLabel="Archive"
        loading={setStatus.isPending}
        confirmTestId="agent-row-archive-confirm"
        description={
          <Text tone="secondary" size="md">
            Archiving “{agent.name}” hides it and its runs from the default lists. Its past
            runs’ cost is still counted in your spending. You can unarchive it at any time.
          </Text>
        }
      />
    </Surface>
  );
}
